(ns ui.components.sortable-list.view
  (:require
   [react :as react]
   [reagent.core :as r]
   ["@atlaskit/pragmatic-drag-and-drop/combine" :refer [combine]]
   ["@atlaskit/pragmatic-drag-and-drop/element/adapter" :refer [draggable dropTargetForElements monitorForElements]]
   ["@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge" :refer [attachClosestEdge extractClosestEdge]]
   ["@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index" :refer [getReorderDestinationIndex]]
   ["@atlaskit/pragmatic-drag-and-drop/reorder" :refer [reorder]]
   ["@atlaskit/pragmatic-drag-and-drop-auto-scroll/element" :refer [autoScrollForElements]]
   [oops.core :as oops]
   [ui.util :as uu]))

(defonce instance-id-counter (atom 0))

(defn get-instance-id
  []
  (swap! instance-id-counter inc))

(defn trigger-post-move-flash
  "Runs a flash animation on the provided element by creating a temporary overlay.
   This animation should be used after an element has been reordered,
   in order to highlight where the element has moved to.
   Uses an overlay approach to ensure the flash is visible regardless of child element styles."
  [element]
  (when element
    (let [overlay (.createElement js/document "div")

          rect (.getBoundingClientRect element)
          computed-style (.getComputedStyle js/window element)

          overlay-styles {:position "absolute"
                          :top "0"
                          :left "0"
                          :right "0"
                          :bottom "0"
                          :background-color "rgba(158, 220, 85, 0.2)"
                          :pointer-events "none"
                          :z-index "9999"
                          :border-radius (.getPropertyValue computed-style "border-radius")}]

      (doseq [[prop value] overlay-styles]
        (oops/oset! overlay (str "style." (name prop)) value))

      ; Ensure the parent element has relative positioning for the overlay
      (let [original-position (.getPropertyValue computed-style "position")]
        (when (= original-position "static")
          (oops/oset! element "style.position" "relative"))

        (.appendChild element overlay)

        (let [animation (oops/ocall
                         overlay
                         "animate"
                         (clj->js [{:opacity "1" :backgroundColor "rgba(158, 220, 85, 0.2)"}
                                   {:opacity "0" :backgroundColor "rgba(158, 220, 85, 0)"}])
                         (clj->js {:duration 600
                                   :easing "cubic-bezier(0.25, 0.1, 0.25, 1.0)"
                                   :iterations 1}))]

          (oops/ocall animation "addEventListener" "finish"
                      (fn []
                        (when (.contains element overlay)
                          (.removeChild element overlay))
                        (when (= original-position "static")
                          (oops/oset! element "style.position" "")))))))))

(defn drag-handle-icon
  [{:keys [width] :or {width 16}}]
  (let [height (* width 1.5)]
    [:svg {:xmlns "http://www.w3.org/2000/svg"
           :width (str width)
           :height (str height)
           :viewBox "0 0 24 24"
           :fill "none"
           :stroke "currentColor"
           :stroke-width "2"
           :stroke-linecap "round"
           :stroke-linejoin "round"}
     [:circle {:cx "9" :cy "12" :r "1"}]
     [:circle {:cx "9" :cy "5" :r "1"}]
     [:circle {:cx "9" :cy "19" :r "1"}]
     [:circle {:cx "15" :cy "12" :r "1"}]
     [:circle {:cx "15" :cy "5" :r "1"}]
     [:circle {:cx "15" :cy "19" :r "1"}]]))

(defn get-item-registry
  []
  (let [registry* (atom {})
        register (fn [{:keys [item-id element]}]
                   (swap! registry* assoc item-id element)

                   (fn []
                     (swap! registry* dissoc item-id)))
        get-element (fn [item-id]
                      (get @registry* item-id))]
    {:register register
     :get-element get-element}))

(defonce item-key (keyword (uu/make-uuid "sortable-item-key-")))

(defn get-item-data
  [{:keys [item index instance-id]}]
  {item-key true
   :item item
   :index index
   :instance-id instance-id})

(defn item-data?
  [data]
  (let [data (if (map? data)
               data
               (js->clj data :keywordize-keys true))]
    (get data item-key)))

(defn drop-indicator
  [{:keys [orientation edge gap appearance indicator-type]
    :or {appearance :default
         gap "0px"
         orientation :vertical}
    :as _args}]

  (when edge
    (let [indicator-type (or indicator-type :terminal)
          stroke-width "2px"
          stroke-color (case appearance
                         :warning "#fbbf24"
                         :default "#9EDC55")

          terminal-diameter "8px"
          terminal-radius "4px"
          indent "0px"

          gap (if (number? gap)
                (str gap "px")
                gap)

          gap-offset (if (= gap "0px")
                       "0px"
                       (str "calc(" gap " / 2)"))

          terminal-position-offset (case indicator-type
                                     :terminal terminal-radius
                                     :terminal-no-bleed "0px"
                                     :no-terminal "0px")

          line-css-vars (merge
                         {"--stroke-color" stroke-color
                          "--stroke-width" stroke-width
                          "--gap-offset" gap-offset
                          "--indent" indent}
                         (case [orientation edge]
                           [:vertical :top] {"--line-top" (str "calc(-" stroke-width " / 2 - " gap-offset ")")}
                           [:vertical :bottom] {"--line-bottom" (str "calc(-" stroke-width " / 2 - " gap-offset ")")}
                           [:horizontal :left] {"--line-left" (str "calc(-" stroke-width " / 2 - " gap-offset ")")}
                           [:horizontal :right] {"--line-right" (str "calc(-" stroke-width " / 2 - " gap-offset ")")}))

          line-classes (str "absolute z-[999] pointer-events-none shadow-md bg-[var(--stroke-color)] "
                            (case [orientation edge]
                              [:vertical :top] "top-[var(--line-top)] left-[var(--indent)] right-0 h-[var(--stroke-width)]"
                              [:vertical :bottom] "bottom-[var(--line-bottom)] left-[var(--indent)] right-0 h-[var(--stroke-width)]"
                              [:horizontal :left] "left-[var(--line-left)] top-[var(--indent)] bottom-0 w-[var(--stroke-width)]"
                              [:horizontal :right] "right-[var(--line-right)] top-[var(--indent)] bottom-0 w-[var(--stroke-width)]"))

          terminal-css-vars (when (not= indicator-type :no-terminal)
                              {"--terminal-diameter" terminal-diameter
                               "--terminal-radius" terminal-radius
                               "--terminal-border-color" stroke-color
                               "--terminal-position-offset" terminal-position-offset
                               "--terminal-left" (if (= orientation :vertical)
                                                   (str "calc(" indent " - " terminal-position-offset ")")
                                                   (str "calc(50% - " terminal-radius ")"))
                               "--terminal-top" (if (= orientation :horizontal)
                                                  (str "calc(" indent " - " terminal-position-offset ")")
                                                  (str "calc(50% - " terminal-radius ")"))})

          terminal-classes (when (not= indicator-type :no-terminal)
                             "absolute w-[var(--terminal-diameter)] h-[var(--terminal-diameter)] border border-2 border-primary-regular rounded-full bg-white box-border z-[1000] left-[var(--terminal-left)] top-[var(--terminal-top)]")]

      [:div {:style line-css-vars
             :class line-classes}
       (when terminal-css-vars
         [:div {:style terminal-css-vars
                :class terminal-classes}])])))

(defn reorder-items
  "Reorders items using the pragmatic drag-and-drop reorder function"
  [items start-index finish-index]
  (js->clj (reorder (clj->js {:list items
                              :startIndex start-index
                              :finishIndex finish-index}))
           :keywordize-keys true))

(def idle-state {:type :idle})

(def dragging-state {:type :dragging})

(defn list-item-inner
  [_]
  (let [element-ref* (atom nil)
        drag-handle-ref* (atom nil)
        draggable-state* (r/atom idle-state)
        closest-edge* (r/atom nil)]
    (fn [{:keys [id index item register-item instance-id
                 drag-handle? orientation gap
                 render-item render-drag-handle
                 container-class item-class style drop-indicator-type]
          :or {drag-handle? true
               orientation :vertical
               gap "0px"
               container-class ""
               item-class ""
               style {}}}]

      (react/useEffect
       (fn []
         (let [element @element-ref*
               drag-handle (if drag-handle?
                             @drag-handle-ref*
                             element)
               data (clj->js
                     (get-item-data
                      {:item item
                       :index index
                       :instance-id instance-id}))

               allowed-edges (case orientation
                               :vertical ["top" "bottom"]
                               :horizontal ["left" "right"])

               on-change (fn [js-args]
                           (let [source (oops/oget js-args "source")
                                 self (oops/oget js-args "self")]
                             (if (= (oops/oget source "element") drag-handle)
                               (reset! closest-edge* nil)
                               (let [closest-edge (extractClosestEdge (oops/oget self "data"))
                                     source-data (oops/oget source "data")
                                     source-index (oops/oget source-data "index")

                                     _ (assert (number? source-index))

                                     is-item-before-source? (= index (dec source-index))
                                     is-item-after-source? (= index (inc source-index))

                                     drop-indicator-hidden? (case orientation
                                                              :vertical (or (and is-item-before-source?
                                                                                 (= closest-edge "bottom"))
                                                                            (and is-item-after-source?
                                                                                 (= closest-edge "top")))
                                                              :horizontal (or (and is-item-before-source?
                                                                                   (= closest-edge "right"))
                                                                              (and is-item-after-source?
                                                                                   (= closest-edge "left"))))]
                                 (if drop-indicator-hidden?
                                   (reset! closest-edge* nil)
                                   (reset! closest-edge* closest-edge))))))]

           (assert element)
           (assert drag-handle)

           (combine
            (register-item
             {:item-id id
              :element element})

            (draggable
             (clj->js
              {:element element
               :dragHandle drag-handle
               :getInitialData (fn [] data)
               :onDragStart (fn [_ev]
                              (reset! draggable-state* dragging-state))
               :onDrop (fn []
                         (reset! draggable-state* idle-state))

               ;; TODO: Maybe enabled later. Could not get this working. Leave as reference
               :onGenerateDragPreview (fn [js-args]
                                        #_(let [location (oops/oget js-args "location")
                                                location-current (oops/oget location "current")
                                                location-current-input (oops/oget location-current "input")
                                                source (oops/oget js-args "source")
                                                rect (-> source
                                                         (oops/oget "element")
                                                         (oops/ocall "getBoundingClientRect"))]
                                            (setCustomNativeDragPreview
                                             (clj->js
                                              {:nativeSetDragImage
                                               (fn [img-element x-offset y-offset]
                                                 (let [img-element (-> img-element
                                                                       (oops/oset! "style.background" "red")
                                                                       (oops/oset! "style.width" "30px")
                                                                       (oops/oset! "style.height" "30px")
                                                                       (oops/oset! "textContent" "Hello test"))]
                                                   (js/console.log "nativeSetDragImage")
                                                   (oops/ocall js-args "nativeSetDragImage" img-element x-offset y-offset)))

                                               :getOffset (fn [js-args]
                                                            (js/console.log "getOffset:" js-args)
                                                            (let [container (oops/oget js-args "container")
                                                                  source-rect (oops/ocall element "getBoundingClientRect")
                                                                  container-rect (oops/ocall container "getBoundingClientRect")
                                                                  cb-fn (preserveOffsetOnSource
                                                                         (clj->js {:element element
                                                                                   :input location-current-input}))
                                                                  result (cb-fn (clj->js {:container container}))]
                                                              result))

                                               :render (fn [js-args]
                                                         (js/console.log "render: start")
                                                         (let [container (oops/oget js-args "container")]
                                                           (reset! draggable-state* {:type :preview
                                                                                     :container container
                                                                                     :rect rect})

                                                           (fn []
                                                             (js/console.log "render: cleanup")
                                                             #_(reset! draggable-state* dragging-state))))}))))}))

            (dropTargetForElements
             (clj->js
              {:element element
               :canDrop (fn [js-args]
                          (let [source (oops/oget js-args "source" "data")]
                            (and (item-data? (js->clj source :keywordize-keys true))
                                 (= (oops/oget source "instance-id") instance-id))))
               :getIsSticky (constantly true)
               :getData (fn [js-args]
                          (let [data (attachClosestEdge
                                      (clj->js data)
                                      (clj->js
                                       {:element element
                                        :input (oops/oget js-args "input")
                                        :allowedEdges allowed-edges}))]
                            data))
               :onDragEnter on-change
               :onDrag on-change
               :onDragLeave #(reset! closest-edge* nil)
               :onDrop #(reset! closest-edge* nil)})))))

       (clj->js [item index register-item instance-id]))

      (let [state @draggable-state*
            state-type (:type state)
            dragging? (= state-type :dragging)
            _preview? (= state-type :preview)
            drag-handle-component (when drag-handle?
                                    (if render-drag-handle
                                      [:div {:ref #(reset! drag-handle-ref* %)}
                                       [render-drag-handle {:dragging? dragging?}]]
                                      [:div
                                       {:ref #(reset! drag-handle-ref* %)
                                        :class "cursor-grab hover:cursor-grab py-1 px-px rounded flex items-center justify-center bg-complement-light hover:bg-complement-regular"}
                                       [drag-handle-icon
                                        {:size 16}]]))]

        [:<>
         [:div
          {:ref #(reset! element-ref* %)
           :class (uu/cm "relative"
                         "hover:translate-x-0"
                         container-class item-class)
           :style (merge {:opacity (if dragging? 0.4 1)}
                         style)}

          [render-item {:item item
                        :index index
                        :dragging? dragging?
                        :drag-handle drag-handle-component}]

          (when @closest-edge*
            [drop-indicator
             {:orientation orientation
              :edge (keyword @closest-edge*)
              :gap gap
              :appearance :default
              :indicator-type drop-indicator-type}])]]))))

(defn list-item
  [props]
  [:f> list-item-inner props])

(defn sortable-list-inner
  [_]
  (let [list-ref* (atom nil)
        registry (get-item-registry)
        instance-id (get-instance-id)]

    (fn [{:keys [items get-key render-item render-drag-handle on-reorder
                 orientation drag-handle? gap container-class item-container-class item-class
                 auto-scroll? drop-indicator-type]
          :or {get-key :id
               orientation :vertical
               drag-handle? false
               gap 0
               item-container-class ""
               item-class ""
               auto-scroll? false}}]
      (let [items (vec items)]

        (react/useEffect
         (fn []
           (let [list-element @list-ref*]
             (assert list-element)
             (combine
              (if auto-scroll?
                (autoScrollForElements
                 (clj->js
                  {:element list-element}))
                identity)

              (monitorForElements
               (clj->js
                {:canMonitor (fn [js-args]
                               (let [source-js (oops/oget js-args "source" "data")
                                     source-clj (js->clj source-js :keywordize-keys true)
                                     valid-item? (item-data? source-clj)
                                     same-instance? (= (:instance-id source-clj) instance-id)
                                     can-monitor? (and valid-item? same-instance?)]
                                 can-monitor?))
                 :onDrop (fn [js-args]
                           (let [source (oops/oget js-args "source" "data")
                                 location (oops/oget js-args "location")
                                 drop-targets (oops/oget location "current" "dropTargets")]
                             (when (-> drop-targets
                                       (oops/oget "length")
                                       (> 0))
                               (let [target (aget drop-targets 0)
                                     source-data (js->clj source :keywordize-keys true)
                                     target-data (js->clj (oops/oget target "data") :keywordize-keys true)]

                                 (when (and (item-data? source-data)
                                            (item-data? target-data))

                                   (let [source-index (:index source-data)
                                         target-index (:index target-data)

                                         closest-edge-of-target (extractClosestEdge (oops/oget target "data"))

                                         final-index (getReorderDestinationIndex
                                                      (clj->js
                                                       {:axis (name orientation)
                                                        :startIndex source-index
                                                        :indexOfTarget target-index
                                                        :closestEdgeOfTarget closest-edge-of-target}))

                                         item (get items source-index)
                                         item-id (get-key item)]

                                     (when (and (fn? on-reorder)
                                                (not= source-index final-index))
                                       (on-reorder {:item item
                                                    :source-index source-index
                                                    :destination-index final-index})
                                       (uu/timeout
                                        4
                                        (fn []
                                          (when-let [element ((:get-element registry) item-id)]
                                            (trigger-post-move-flash element)))))))))))})))))

         (clj->js [instance-id orientation on-reorder]))

        [:div
         {:ref #(reset! list-ref* %)
          :class (uu/cm "grow h-full"
                        (case orientation
                          :vertical "flex flex-col overflow-x-visible overflow-y-visible"
                          :horizontal "flex flex-row")
                        (when auto-scroll?
                          (case orientation
                            :vertical "overflow-y-scroll overflow-x-visible"
                            :horizontal "overflow-x-scroll overflow-y-auto"))
                        container-class)
          :style {:gap (str (or gap 0) "px")}}
         (->> items
              (map-indexed
               (fn [index item]
                 (let [item-id (get-key item)]
                   ^{:key (str item-id)}
                   [list-item
                    {:id item-id
                     :item item
                     :index index
                     :instance-id instance-id
                     :register-item (:register registry)
                     :orientation orientation
                     :drag-handle? drag-handle?
                     :gap gap
                     :render-item render-item
                     :render-drag-handle render-drag-handle
                     :container-class (uu/cm item-container-class)
                     :drop-indicator-type drop-indicator-type
                     :item-class (uu/cm item-class)}]))))]))))

(defn sortable-list
  [props]
  [:f> sortable-list-inner props])

(defn view
  [props]
  [sortable-list props])
